import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { IConversationDetails } from '../../../../models/conversation-details.model';
import { IContactDetails } from '../../../../models/contact-details.model';
import { IAppointmentDetails } from '../../../../models/appointment-details.model';
import { ILeasingPreferences } from '../../../../models/leasing-preferences.model';
import { ICampaignDetails } from '../../../../models/campaign-details.model';
import { PhoneNumberPipe } from '../../../../pipes/phone-number.pipe';
import { HasPetsLabelPipe } from '../../../../../components/conversation-data/pipes/has-pets-label.pipe';
import { MonthsAmountPipe } from '../../../../../components/conversation-data/pipes/months-amount.pipe';
import { DesiredPriceRangePipe } from '../../../../../components/conversation-data/pipes/desired-price-range.pipe';
import { TimePipe } from '../../../../pipes/time.pipe';
import { FullNamePipe } from '../../../../pipes/full-name.pipe';

@Component({
  selector: 'cv-pdf-details',
  standalone: true,
  templateUrl: './pdf-details.component.html',
  styleUrl: '../../pdf-styles.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfDetailsComponent {
  constructor(
    private datePipe: DatePipe,
    private timePipe: TimePipe,
    private phoneNumberPipe: PhoneNumberPipe,
    private hasPetsLabelPipe: HasPetsLabelPipe,
    private monthsAmountLabelPipe: MonthsAmountPipe,
    private desiredPriceRangePipe: DesiredPriceRangePipe,
    private fullNamePipe: FullNamePipe,
  ) {}

  @Input() contactDetails: IContactDetails | null = null;
  @Input() appointmentDetails: IAppointmentDetails | null = null;
  @Input() leasingPreferences: ILeasingPreferences | null = null;
  @Input() campaignDetails: ICampaignDetails | null = null;
  @Input() conversationDetails: IConversationDetails | null = null;

  get contactDetailsMap(): [string, any][] {
    return this.contactDetails
      ? Array.from(
          new Map<string, any>([
            ['Name', this.fullNamePipe.transform(this.contactDetails.firstName, this.contactDetails.lastName)],
            ['Contact Type', this.contactDetails.inferredContactType],
            ['Contact Subtype', this.contactDetails.inferredContactSubtype],
            ['Qualified Prospect', this.contactDetails.qualifiedProspect ? 'Yes' : 'No'],
            ['Caller ID', this.phoneNumberPipe.transform(this.contactDetails.callerId)],
            ['Provided Phone Number', this.phoneNumberPipe.transform(this.contactDetails.providePhone)],
            ['Email', this.contactDetails.email],
          ]),
        )
      : [];
  }

  get appointmentDetailsMap(): [string, any][] {
    return this.appointmentDetails
      ? Array.from(
          new Map<string, any>([
            ['Appointment Type', this.appointmentDetails.tourInformation.tourType],
            ['Tour Date', this.datePipe.transform(this.appointmentDetails.tourInformation.tourDate, 'M/d/yyyy')],
            ['Tour Time', this.timePipe.transform(this.appointmentDetails.tourInformation.tourTime, 'h:mm a')],
          ]),
        )
      : [];
  }

  get leasingPreferencesMap(): [string, any][] {
    return this.leasingPreferences
      ? Array.from(
          new Map<string, any>([
            ['Desired Bedrooms', this.leasingPreferences.desiredBedrooms],
            ['Desired Bathrooms', this.leasingPreferences.desiredBathrooms],
            ['Number of Occupants', this.leasingPreferences.numberOfOccupants],
            ['Desired Lease Terms', this.monthsAmountLabelPipe.transform(this.leasingPreferences.desiredLeaseTerms)],
            [
              'Desired Price Range',
              this.desiredPriceRangePipe.transform(
                this.leasingPreferences.desiredPriceRangeFrom,
                this.leasingPreferences.desiredPriceRangeTo,
              ),
            ],
            ['Desired Move-In Date', this.leasingPreferences.moveInDate],
            ['Has Pets', this.hasPetsLabelPipe.transform(this.leasingPreferences.hasPets)],
            ['Amenity Interests', this.leasingPreferences.amenityInterests],
          ]),
        )
      : [];
  }

  get campaignDetailsMap(): [string, any][] {
    return this.campaignDetails
      ? Array.from(
          new Map<string, any>([
            ['Ad Source', this.campaignDetails.adsourceName],
            ['Category', this.campaignDetails.categoryTypeName],
            ['Campaign', this.campaignDetails.campaignName],
            ['Details', this.campaignDetails.details],
          ]),
        )
      : [];
  }

  get conversationDetailsMap(): [string, any][] {
    return this.conversationDetails
      ? Array.from(
          new Map<string, any>([
            ['Account', this.conversationDetails.accountName],
            ['Site', this.conversationDetails.siteName],
            ['Answering Agent', `${this.conversationDetails.agentFirstName} ${this.conversationDetails.agentLastName}`],
            ['Start Time', this.datePipe.transform(this.conversationDetails.startDate, 'M/d/yyyy h:mm a')],
            ['Duration', this.conversationDetails.callDuration],
            ['Communication Type', this.conversationDetails.communicationTypeName],
            ['Communication ID', this.conversationDetails.conversationId],
            ['Call ID', this.conversationDetails.callDetailRecordId],
          ]),
        )
      : [];
  }
}
